<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {computed, onMounted, ref, watch} from "vue";
import moment from "moment/moment";

const props = defineProps({
  range: {
    type: Array,
    required: true,
  },
  compareRange: {
    type: Array,
    required: false,
    default: () => [],
  },
  variant: {
    type: String,
    required: false,
    default: 'primary',
    validator: (value,) => ['primary', 'secondary'].includes(value),
  }
})

const emit = defineEmits(['change', 'close']);

const today = computed(() => moment());

const currentDate = ref(moment());

const hoveredDate = ref();

const localFromDate = ref();
const localToDate = ref();

const propsFromDate = computed(() => props.range[0] ?? undefined);
const propsToDate = computed(() => props.range[1] ?? undefined);

const compareFromDate = computed(() => {
  const date = props.compareRange[0] ?? undefined;

  return date ? moment(date) : undefined;
});

const compareToDate = computed(() => {
  const date = props.compareRange[1] ?? undefined;

  return date ? moment(date) : undefined;
});

const setLocalFromProps = () => {
  localFromDate.value = propsFromDate.value ? moment(propsFromDate.value) : undefined;
  localToDate.value = propsToDate.value ? moment(propsToDate.value) : undefined;
}

watch([propsFromDate, propsToDate], () => {
  // When the prop dates change update local
  setLocalFromProps()
}, {immediate: true});

watch([localFromDate, localToDate], () => {
  if (!localFromDate.value || !localToDate.value) return;

  // Ensure from date is always before to date
  if (localFromDate.value.isAfter(localToDate.value)) {
    const fromDateCopy = localFromDate.value.clone();
    localFromDate.value = localToDate.value.clone();
    localToDate.value = fromDateCopy.clone();
  }
});

const dates = computed(() => {
  const start = currentDate.value.clone().startOf('month');
  const end = currentDate.value.clone().endOf('month');
  let date = start.clone();
  const results = [];
  while (date.isSameOrBefore(end)) {
    results.push(date.clone());
    date.add(1, 'day');
  }

  // Pad the start of the calendar so dates fall under weekday cols correctly
  const daysFromMonday = results[0].day() - 1;

  return [
    ...Array(daysFromMonday > 0 ? daysFromMonday : 0),
    ...results,
  ];
});

const filteredDates = computed(() => dates.value.filter((date) => date));


const daysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const color = computed(() => {
  if (props.variant === 'secondary') return 'info';

  return 'primary';
});

const compareColor = computed(() => {
  if (props.variant === 'secondary') return 'primary';

  return 'info';
});
const handleDateClick = (date) => {
  if (localToDate.value) {
    localFromDate.value = date;
    localToDate.value = undefined;
    return;
  }

  if (!localFromDate.value) {
    localFromDate.value = date;
    return;
  }

  localToDate.value = date;
}

const submit = () => {
  emit('change', [
    localFromDate.value?.format(),
    localToDate.value?.format() ?? localFromDate.value?.format(),
  ]);
  emit('close');
}

const closePicker = () => {
  setLocalFromProps();
  emit('close');
}

const dateIsInRange = (date) => {
  if (date?.isSameOrAfter(localFromDate.value, 'day') && !!localToDate.value && date?.isSameOrBefore(localToDate.value, 'day')) {
    return true
  }

  // Return true if date is in current hovered range
  return ((date?.isSameOrAfter(localFromDate.value))
    && !localToDate.value
    && hoveredDate.value && date?.isSameOrBefore(hoveredDate.value));
}

const dateIsInCompareRange = (date) => {
  if (!date) return false;

  if (!compareFromDate.value || !compareToDate.value) return false;

  return date.isSameOrAfter(compareFromDate.value, 'day') && date.isSameOrBefore(compareToDate.value, 'day');
}

const dateInCompareOverlap = (date) => {
  return date?.isSame(localFromDate.value, 'day') && dateIsInCompareRange(date);
};
const dateInCurrentOverlap = (date) => {
  if (!localToDate.value && hoveredDate.value && date?.isSame(hoveredDate.value, 'day') && dateIsInRange(date) && dateIsInCompareRange(date)) return true;

  return date?.isSame(localToDate.value, 'day') && dateIsInRange(date) && dateIsInCompareRange(date);
};

const alwaysRoundLeftSide = (date) => {
  // Round all Mondays
  if (date?.day() === 1) return true;

  // Round all first days of month in calendar
  if (date?.isSame(filteredDates.value[0], 'day')) return true;

  return (date?.isSame(localFromDate.value, 'day') && (!compareFromDate.value || date.isSame(compareFromDate.value, 'day')));
}

const shouldRoundLeftSide = (date) => {
  // Don't round any dates that are during compare range (exclusive of start and end)
  if (compareFromDate.value && compareToDate.value && (date.isAfter(compareFromDate.value, 'day') && date.isBefore(compareToDate.value, 'day'))) return false;

  // Don't round any dates that are during current range (exclusive of start and end)
  if ((date.isAfter(localFromDate.value, 'day') && localToDate.value && date.isBefore(localToDate.value, 'day'))) return false;

  // Don't round dates within new hovered range
  if (!localToDate.value && hoveredDate.value && date.isSameOrBefore(hoveredDate.value) && date.isAfter(localFromDate.value)) return false;

  // Round from-dates
  return (date.isSame(localFromDate.value, 'day') || (!!compareFromDate.value && date.isSame(compareFromDate.value, 'day')));
}

const alwaysRoundRightSide = (date) => {
  // Round all Sundays
  if (date?.day() === 0) return true;

  // Round all last days of month in calendar
  if (date?.isSame(filteredDates.value[filteredDates.value.length - 1], 'day')) return true;

  if ((!localToDate.value && hoveredDate.value && date?.isSame(hoveredDate.value, 'day') && (!compareToDate.value || date.isSame(compareToDate.value, 'day')))) return true;

  return (localToDate.value && date?.isSame(localToDate.value, 'day') && (!compareToDate.value || date.isSame(compareToDate.value, 'day')));
}

const shouldRoundRightSide = (date) => {
  // Don't round any dates that are during compare range (exclusive of start and end)
  if (compareFromDate.value && compareToDate.value && (date.isAfter(compareFromDate.value, 'day') && date.isBefore(compareToDate.value, 'day'))) return false;

  // Don't round dates before the hover date if selecting new to date
  if (!localToDate.value && hoveredDate.value && date.isBefore(hoveredDate.value) && date.isSameOrAfter(localFromDate.value)) return false;

  // Round if hovered over and currently selecting new to-date
  if (!localToDate.value && compareToDate.value && date.isSame(compareToDate.value, 'day') && (!hoveredDate.value || (hoveredDate.value && date.isSameOrBefore(hoveredDate.value)))) return true;

  // Round if current date is hover date and there is no comparison date
  if (!localToDate.value && !compareToDate.value && (hoveredDate.value || (hoveredDate.value && date.isSame(hoveredDate.value)))) return true;

  // Round if current date is hover date and the comparison date is after hover date
  if (!localToDate.value && compareToDate.value && date.isSameOrAfter(compareToDate.value) && (hoveredDate.value || (hoveredDate.value && date.isSame(hoveredDate.value)))) return true;

  if (!localToDate.value && !dateIsInCompareRange(date) && hoveredDate.value && (hoveredDate.value && date.isSame(hoveredDate.value))) return true;

  // Don't round any dates that are during compare range (exclusive of start and end)
  if ((date.isAfter(localFromDate.value, 'day') && (localToDate.value && date.isBefore(localToDate.value, 'day')))) return false;

  // Round to dates
  return (localToDate.value && date.isSame(localToDate.value, 'day')) || (compareToDate.value && date.isSame(compareToDate.value, 'day'));
}

const highlightDate = (date) => (localFromDate.value && date?.isSame(localFromDate.value, 'day')) || (localToDate.value && date?.isSame(localToDate.value, 'day'));

const isDisabled = (date) => {
  if (!date) return true;

  if (date.isAfter(today.value)) return true;

  if (localFromDate.value && !localToDate.value && date?.isBefore(localFromDate.value)) {
    return true;
  }

  if (props.variant === 'primary') return false;

  return !!(compareToDate.value && date.isAfter(compareToDate.value));
}

const getStylesForDateBg = (date) => {
  const classes = [];
  if (!date) return classes;

  // Apply background color for comparison date range
  if (dateIsInCompareRange(date) && !dateIsInRange(date)) {
    classes.push(`tw-bg-${compareColor.value}-100`);
  }
  // Apply background color for current date range
  if (dateIsInRange(date) && date.isSameOrBefore(today, 'day')){
    classes.push(`tw-bg-${color.value}-100`)
  }

  // Apply gradient when compare date range overlaps current date range
  const overlapsCompare = dateInCompareOverlap(date);
  if (overlapsCompare) {
    classes.push(`tw-bg-gradient-to-r tw-from-${compareColor.value}-100 tw-to-${color.value}-100`);
  }

  // Apply gradient when current date range overlaps compare date range
  const overlapsCurrent = dateInCurrentOverlap(date);
  if (overlapsCurrent) {
    classes.push(`tw-bg-gradient-to-r tw-from-${color.value}-100 tw-to-${compareColor.value}-100`);
  }

  if (alwaysRoundLeftSide(date) || (shouldRoundLeftSide(date) && !overlapsCompare && !overlapsCurrent)) {
    classes.push('tw-rounded-l-full');
  }

  if (alwaysRoundRightSide(date) || (shouldRoundRightSide(date) && !overlapsCompare && !overlapsCurrent)) {
    classes.push('tw-rounded-r-full');
  }

  return classes;
}

onMounted(() => {
  if (!localFromDate.value) return;

  // Ensure end date is in view when picker opens
  currentDate.value = localFromDate.value.clone();
});

</script>

<template>
  <div
    class="tw-fixed tw-bg-transparent tw-z-50 tw-top-0 tw-left-0 tw-w-full tw-h-full"
    @click="closePicker"
  />

  <div
    class="tw-absolute tw-top-20 tw-left-4 tw-bg-white tw-p-6 tw-rounded-lg tw-border tw-drop-shadow-lg tw-z-50"
  >
    <div class="tw-flex tw-gap-4 tw-justify-between tw-items-center tw-mb-4">
      <DefaultButton
        variant="primary-inverted"
        class="!tw-px-3 !tw-py-3 tw-border"
        title="Previous month"
        @click="currentDate = currentDate.clone().subtract(1, 'month')"
      >
        <svg-vue
          icon="icons/chevron"
          class="tw-h-3 tw-w-3 tw-rotate-90"
        />
      </DefaultButton>

      <span class="tw-font-semibold tw-text-xl tw-text-font-primary tw-antialiased">{{ currentDate.format('MMMM YYYY') }}</span>

      <DefaultButton
        variant="primary-inverted"
        class="!tw-px-3 !tw-py-3 tw-border"
        title="Next month"
        @click="currentDate = currentDate.clone().add(1, 'month')"
      >
        <svg-vue
          icon="icons/chevron"
          class="tw-h-3 tw-w-3 -tw-rotate-90"
        />
      </DefaultButton>
    </div>

    <div class="tw-grid tw-grid-cols-7 tw-gap-y-1 tw-mb-6">
      <div
        v-for="(day, index) in daysOfWeek"
        :key="`${day}_${index}`"
        class="tw-text-center tw-h-10 tw-w-10"
      >
        <span>{{ day }}</span>
      </div>
      <div
        v-for="(date, index) in dates"
        :key="date?.unix() ?? index"
        :class="[
          'tw-text-center tw-relative tw-h-10 tw-w-10',
          ...getStylesForDateBg(date),
        ]"
      >
        <button
          v-if="date"
          :disabled="isDisabled(date)"
          :class="[
            'tw-py-2 tw-px-3 tw-transition-all tw-text-center tw-block tw-w-full tw-h-full tw-rounded-full hover:tw-text-white disabled:!tw-bg-transparent hover:disabled:!tw-bg-transparent disabled:!tw-text-grey',
            highlightDate(date) ? `tw-bg-${color} tw-text-white tw-rounded-full` : `hover:tw-bg-${color}`,
            {
              'tw-font-semibold' : date?.isSame(today, 'day'),
            },
          ]"
          @click="() => handleDateClick(date)"
          @mouseover="hoveredDate = date?.clone()"
        >
          {{ date?.date() }}
        </button>
        <span v-else />
      </div>
    </div>

    <div class="tw-flex">
      <DefaultButton
        variant="primary"
        class="tw-ml-auto !tw-py-3 !tw-px-8 tw-font-normal tw-text-sm"
        @click="submit"
      >
        Apply
      </DefaultButton>
    </div>
  </div>
</template>
